import { useQuery } from "@tanstack/react-query"
import { api } from "@/api"

export const PERSONALIZED_SUMMARY_QUERY_KEY = "personalized-summary"

export interface PersonalizedSummary {
    id: string
    summary: string
    billId: string
    billTitle: string
    profileId: string
    profileName: string
    createdAt: string
}

interface PersonalizedSummaryAPI {
    id: string
    bill_id: string
    bill_title: string
    summary: string
    profile_id: number
    profile_name: string
    created_at: string
    is_outdated: boolean
}

interface UsePersonalizedSummaryParams {
    billId?: string
    profileId?: string
    boardId?: string
    nextBillId?: string
    enabled?: boolean
}

// API function to fetch existing personalized summary
const fetchPersonalizedSummary = async (
    billId: string,
    boardId?: string,
    profileId?: string,
    nextBillId?: string,
): Promise<PersonalizedSummary | null> => {
    const params = new URLSearchParams()
    if (nextBillId) {
        params.set("next_bill", nextBillId)
    }

    if (boardId) {
        params.append("ai_tracking_board_id", boardId)
    }

    if (profileId) {
        params.append("personalization_profile_id", profileId)
    }

    const response = await api.get(`/api/personalization/bill-summaries/${billId}?${params.toString()}`)

    const data = await response.json()

    if (!data.summary) {
        return null
    }

    return {
        id: data.summary.id,
        summary: data.summary.content,
        billId: data.summary.bill_id,
        billTitle: data.summary.bill_title,
        profileId: data.summary.profile_id,
        profileName: data.summary.profile_name,
        createdAt: data.summary.created_at,
    }
}

export const usePersonalizedSummary = ({
    billId,
    boardId,
    profileId,
    nextBillId,
    enabled = true,
}: UsePersonalizedSummaryParams) => {
    return useQuery({
        queryKey: [PERSONALIZED_SUMMARY_QUERY_KEY, billId, boardId, profileId, nextBillId],
        queryFn: () => {
            if (!billId) return null
            return fetchPersonalizedSummary(billId, boardId, profileId, nextBillId)
        },
        staleTime: 5 * 60 * 1000,
        enabled,
    })
}
